import { useEffect, useState } from "react";
import { Metadata } from "../interfaces/metadata";

/** Metadata key for the configurable app service fee (pickup) shared across entities. */
export const APP_SERVICE_FEE_KEY: string = "app_service_fee";

/** Metadata key for the seat-delivery fixed service fee. */
export const APP_DELIVERY_FEE_KEY: string = "app_service_fee_delivery";

/** Metadata key for the subtotal threshold at/above which seat delivery is free. */
export const APP_DELIVERY_FREE_THRESHOLD_KEY: string = "app_delivery_free_threshold";

/** Metadata key for the pickup fee type: a flat amount or a percentage of the cart subtotal. */
export const APP_SERVICE_FEE_TYPE_KEY: string = "app_service_fee_type";

/** Metadata key for the seat-delivery fee type: a flat amount or a percentage of the cart subtotal. */
export const APP_DELIVERY_FEE_TYPE_KEY: string = "app_service_fee_delivery_type";

/** Metadata key that toggles in-app ordering for an event (master gate; absent means disabled). */
export const APP_ORDER_ENABLED_KEY: string = "app_order_enabled";

/** Metadata key that toggles pre-ordering for an event (sub-gate of in-app ordering). */
export const APP_PRE_ORDER_ENABLED_KEY: string = "app_pre_order_enabled";

/** Fee configuration keys (amounts, types and threshold) an event can inherit-preload from its venue. */
export const APP_ORDER_FEE_CONFIG_KEYS: string[] = [
  APP_SERVICE_FEE_KEY,
  APP_SERVICE_FEE_TYPE_KEY,
  APP_DELIVERY_FEE_KEY,
  APP_DELIVERY_FEE_TYPE_KEY,
  APP_DELIVERY_FREE_THRESHOLD_KEY,
];

/** Maximum commission amount (pickup and seat delivery) an event may configure. */
export const APP_ORDER_FEE_MAX: number = 999999;

/** Example-value placeholder for monetary fee inputs, distinct from their labels. */
export const APP_SERVICE_FEE_PLACEHOLDER: string = "0.00";

export type AppServiceFeeType = "fixed" | "percentage";

/**
 * Maps each fee value key to the metadata key that stores its fixed/percentage type.
 * The pickup and seat-delivery fees support a type; the free-delivery threshold is a plain
 * amount. Fee keys absent from this map are treated as plain amounts (no type persisted).
 */
const FEE_TYPE_KEY_BY_FEE_KEY: Record<string, string> = {
  [APP_SERVICE_FEE_KEY]: APP_SERVICE_FEE_TYPE_KEY,
  [APP_DELIVERY_FEE_KEY]: APP_DELIVERY_FEE_TYPE_KEY,
};

/** Maximum number of decimals accepted for fee inputs. */
const MAX_FEE_DECIMALS: number = 4;

/** Upper bound for percentage fees (percent of the cart subtotal). */
const MAX_PERCENTAGE_VALUE: number = 100;

export interface UseAppServiceFeeResult {
  feeMetadataInfo: Metadata | undefined;
  feeDisplay: string;
  feeType: AppServiceFeeType;
  handleFeeChange: (value: string) => void;
  handleFeeTypeChange: (type: AppServiceFeeType) => void;
}

/**
 * Controls one "app service fee" metadata pair (value + type), surfaced in the dedicated
 * "Pedidos" tab and hidden from "Opc. Avanzadas".
 *
 * Shared between the Clients and Events forms. An empty input means "inherit"
 * (both keys are removed from metadata); any value, including 0, overrides the
 * inherited fee. The type is persisted together with the value so a level is never
 * half-configured: with no value set, the selected type stays local until a value exists.
 *
 * @param entityMetadata Metadata definitions for the entity being edited.
 * @param metadata Current metadata map held in the form state.
 * @param onMetadataChange Callback to persist the updated metadata map.
 * @param feeKey Metadata key this instance controls (defaults to the pickup app service fee).
 * @returns The fee metadata definition, the controlled display value and type, and the change handlers.
 */
export default function useAppServiceFee(
  entityMetadata: Metadata[],
  metadata: Record<string, any> | undefined,
  onMetadataChange: (metadata: Record<string, any>) => void,
  feeKey: string = APP_SERVICE_FEE_KEY,
  maxAmount?: number,
): UseAppServiceFeeResult {
  const feeMetadataInfo: Metadata | undefined = entityMetadata.find(
    (option: Metadata) => option.key === feeKey,
  );
  // The fixed/percentage type only applies to fee keys registered in the map; the
  // free-delivery threshold (and, for now, the seat-delivery fee) are plain amounts.
  const feeTypeKey: string | undefined = FEE_TYPE_KEY_BY_FEE_KEY[feeKey];
  const feeStoredValue: string | number | boolean | undefined =
    metadata?.[feeKey];
  const feeStoredType: string | number | boolean | undefined = feeTypeKey
    ? metadata?.[feeTypeKey]
    : undefined;
  const [feeDisplay, setFeeDisplay] = useState<string>(
    feeStoredValue !== undefined && feeStoredValue !== null
      ? String(feeStoredValue)
      : "",
  );
  const [feeType, setFeeType] = useState<AppServiceFeeType>(
    feeStoredType === "percentage" ? "percentage" : "fixed",
  );

  useEffect(() => {
    if (feeStoredValue !== undefined && feeStoredValue !== null) {
      setFeeDisplay((prev: string) =>
        parseFloat(prev) === Number(feeStoredValue)
          ? prev
          : String(feeStoredValue),
      );
    } else {
      setFeeDisplay("");
    }
  }, [feeStoredValue]);

  useEffect(() => {
    setFeeType(feeStoredType === "percentage" ? "percentage" : "fixed");
  }, [feeStoredType]);

  const handleFeeChange: (value: string) => void = (value) => {
    const nextMetadata: Record<string, string | number | boolean> = {
      ...metadata,
    };
    // Empty => inherit: drop the pair (a value, incl. 0, overrides).
    if (value === "" || value === null || value === undefined) {
      delete nextMetadata[feeKey];
      if (feeTypeKey) {
        delete nextMetadata[feeTypeKey];
      }
      setFeeDisplay("");
      onMetadataChange(nextMetadata);
      return;
    }
    if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
      return;
    }
    const parts: string[] = value.split(".");
    if (parts.length === 2 && parts[1].length > MAX_FEE_DECIMALS) {
      return;
    }
    const numericValue: number = parseFloat(value);
    if (
      feeType === "percentage" &&
      Number.isFinite(numericValue) &&
      numericValue > MAX_PERCENTAGE_VALUE
    ) {
      return;
    }
    if (
      maxAmount !== undefined &&
      Number.isFinite(numericValue) &&
      numericValue > maxAmount
    ) {
      return;
    }
    setFeeDisplay(value);
    if (Number.isFinite(numericValue)) {
      nextMetadata[feeKey] = numericValue;
      if (feeTypeKey) {
        nextMetadata[feeTypeKey] = feeType;
      }
    } else {
      delete nextMetadata[feeKey];
      if (feeTypeKey) {
        delete nextMetadata[feeTypeKey];
      }
    }
    onMetadataChange(nextMetadata);
  };

  const handleFeeTypeChange: (type: AppServiceFeeType) => void = (type) => {
    setFeeType(type);
    const storedNumericValue: number = parseFloat(String(feeStoredValue));
    // No stored value yet: keep the type local until a value is entered (never persist a lone type).
    if (!Number.isFinite(storedNumericValue)) {
      return;
    }
    const boundedValue: number =
      type === "percentage" && storedNumericValue > MAX_PERCENTAGE_VALUE
        ? MAX_PERCENTAGE_VALUE
        : storedNumericValue;
    if (boundedValue !== storedNumericValue) {
      setFeeDisplay(String(boundedValue));
    }
    const nextMetadata: Record<string, string | number | boolean> = {
      ...metadata,
      [feeKey]: boundedValue,
    };
    if (feeTypeKey) {
      nextMetadata[feeTypeKey] = type;
    }
    onMetadataChange(nextMetadata);
  };

  return {
    feeMetadataInfo,
    feeDisplay,
    feeType,
    handleFeeChange,
    handleFeeTypeChange,
  };
}
